import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import PermissionedComponent from '~/components/Common/PermissionedComponent';
import UserSearchWidget from '~/components/Common/UserSearchWidget';

import { ROLE_PROMO_ADMIN_USER } from '~/consts/roles';

import { useGetLanguageOptions } from '~/hooks/apis/lab/useGetLanguageOptions';
import { useGetMyVouchers } from '~/hooks/apis/promo/useGetMyVouchers';

import VoucherCountPreview from './VoucherCountPreview';
import VoucherPublicPreviewCard from './VoucherPublicPreviewCard';

type Props = {
  tenant: App.Tenant;
  /** When supplied, this user is previewed directly and the search control is omitted. */
  userId?: string;
};

const VOUCHER_STATUSES = ['active', 'redeemed'] as const;
type VoucherStatus = (typeof VOUCHER_STATUSES)[number];
const DEFAULT_LOCALE = 'en-AU';

function isVoucherStatus(value: unknown): value is VoucherStatus {
  return value === 'active' || value === 'redeemed';
}

function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

/**
 * Debug view of the vouchers a customer would receive from the account /my-vouchers endpoint.
 * It deliberately reuses the public voucher card so the primary view mirrors account presentation,
 * while retaining the full response for operational investigation.
 */
export default function VoucherAccountPreview({ tenant, userId }: Props) {
  const fixedUserId = userId?.trim() ?? '';
  const [selectedUserId, setSelectedUserId] = useState(fixedUserId);
  const [searchValue, setSearchValue] = useState('');
  const [region, setRegion] = useState('AU');
  const [locale, setLocale] = useState(DEFAULT_LOCALE);
  const [status, setStatus] = useState<VoucherStatus>('active');
  const previewUserId = fixedUserId || selectedUserId.trim();
  const { data: languageOptions = [] } = useGetLanguageOptions();
  const regions = useMemo(() => getRegions(tenant.brand), [tenant.brand]);
  const localeOptions = useMemo(
    () =>
      languageOptions.some((option) => option.value === DEFAULT_LOCALE)
        ? languageOptions
        : [{ value: DEFAULT_LOCALE, label: 'English (Australia)' }, ...languageOptions],
    [languageOptions],
  );
  const { data, error, isFetching, refetch } = useGetMyVouchers({
    brand: tenant.brand,
    userId: previewUserId,
    region,
    locale,
    status,
  });

  useEffect(() => {
    if (fixedUserId) {
      setSelectedUserId(fixedUserId);
    }
  }, [fixedUserId]);

  useEffect(() => {
    if (!regions.some((candidate) => candidate.code === region)) {
      setRegion(regions[0]?.code ?? 'AU');
    }
  }, [region, regions]);

  const handleUserSelected = useCallback((_id: string, value: string) => {
    setSelectedUserId(value);
  }, []);

  const handleLoad = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleStatusChange = useCallback((_event: React.SyntheticEvent, value: unknown) => {
    if (isVoucherStatus(value)) {
      setStatus(value);
    }
  }, []);

  const vouchers = data?.result?.vouchers ?? [];
  const errorMessage = error instanceof Error ? error.message : 'Unable to load account vouchers.';

  return (
    <Stack spacing={2}>
      {!fixedUserId ? (
        <UserSearchWidget
          id="voucher-account-preview-user-search"
          label="Name / Email / Customer Support Code / User ID"
          formErrors={[]}
          onChange={handleUserSelected}
          passedAutocompleteValue={searchValue}
          setPassedAutocompleteValue={setSearchValue}
        />
      ) : null}

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          select
          label="Region"
          value={region}
          onChange={(event) => setRegion(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {regions.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.name} ({option.code})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Locale"
          value={locale}
          onChange={(event) => setLocale(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          {localeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label} ({option.value})
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={handleLoad} disabled={!previewUserId || isFetching}>
          {isFetching ? 'Loading…' : `Load ${status} vouchers`}
        </Button>
        {previewUserId ? (
          <Typography variant="body2" color="text.secondary">
            User ID: {previewUserId}
          </Typography>
        ) : null}
      </Stack>

      {!previewUserId ? <Alert severity="info">Search for a user to inspect their account voucher state.</Alert> : null}
      <PermissionedComponent requiredRoles={[ROLE_PROMO_ADMIN_USER]}>
        <VoucherCountPreview brand={tenant.brand} userId={previewUserId} region={region} />
      </PermissionedComponent>
      <Tabs value={status} onChange={handleStatusChange} aria-label="Voucher status">
        <Tab label="Active" value="active" />
        <Tab label="Redeemed" value="redeemed" />
      </Tabs>
      {isFetching && !data ? <CircularProgress size={24} /> : null}
      {error ? <Alert severity="error">{errorMessage}</Alert> : null}

      {data ? (
        <>
          <Typography variant="body2" color="text.secondary">
            {data.result.total ?? vouchers.length} {status} voucher{data.result.total === 1 ? '' : 's'} returned by the
            account endpoint for {region} in {locale}.
          </Typography>
          {vouchers.length > 0 ? (
            <Stack spacing={2}>
              {vouchers.map((voucher) => (
                <Accordion key={`${voucher.voucherId}-${voucher.voucherRewardId ?? 'default'}`} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">{voucher.voucherTitle || voucher.voucherName}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <VoucherPublicPreviewCard voucher={voucher} isRedeemed={status === 'redeemed'} />
                      <Box
                        component="pre"
                        aria-label={`${voucher.voucherName} debug details`}
                        sx={{
                          m: 0,
                          overflow: 'auto',
                          p: 2,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          fontSize: 12,
                        }}
                      >
                        {formatJson(voucher)}
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No {status} vouchers were returned for this user.</Alert>
          )}
          <Accordion variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Response metadata and debug details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{ m: 0, overflow: 'auto', p: 2, bgcolor: 'background.default', borderRadius: 1, fontSize: 12 }}
              >
                {formatJson(data)}
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      ) : null}
    </Stack>
  );
}
